import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { validatePhoneNumber } from '../api/api';
import './Modal.css';

const PaymentModal = ({ isOpen, onClose, onSubmit, item, isProcessing }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: { duration: 0.2 }
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
      setError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Format phone number for validation
    const formattedPhone = phoneNumber.startsWith('0') ? phoneNumber : `0${phoneNumber}`;
    
    if (!validatePhoneNumber(formattedPhone)) {
      setError('Please enter a valid Kenyan phone number (e.g., 0712345678)');
      return;
    }
    
    onSubmit(formattedPhone);
  };

  const handleClose = () => {
    if (!isProcessing) {
      setPhoneNumber('');
      setError('');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={handleClose}
        >
          <motion.div
            className="modal-content payment-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="mpesa-logo">
                <span className="mpesa-m">M</span>
                <span className="mpesa-pesa">-PESA</span>
              </div>
              <h2 className="modal-title">Enter Payment Details</h2>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="payment-summary">
                <div className="summary-item">
                  <span>{item?.name}</span>
                  <span className="summary-amount">KES {item?.cost}</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber" className="form-label">
                  M-Pesa Phone Number
                </label>
                <div className="phone-input-wrapper">
                  <span className="phone-prefix">+254</span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    className={`form-input phone-input ${error ? 'error' : ''}`}
                    placeholder="712345678"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    disabled={isProcessing}
                    autoComplete="tel"
                  />
                </div>
                <AnimatePresence>
                  {error && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      {error}
                    </motion.span>
                  )}
                </AnimatePresence>
                <span className="form-hint">
                  Enter your Safaricom number to receive STK push
                </span>
              </div>

              {isProcessing && (
                <motion.div
                  className="processing-indicator"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="processing-spinner" />
                  <p>Sending payment request to your phone...</p>
                  <p className="processing-hint">Please enter your M-Pesa PIN when prompted</p>
                </motion.div>
              )}

              <div className="modal-footer">
                <motion.button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isProcessing}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className="btn btn-primary btn-mpesa"
                  disabled={isProcessing || phoneNumber.length < 9}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isProcessing ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <span>Pay KES {item?.cost}</span>
                      <span className="btn-icon">📱</span>
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;

